import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from 'src/store/store';
import {
  clearErrors,
  loadData,
  removeSuccessMsg,
  handleSignatureSubmit as validateSignature,
} from 'src/features/settings/signatureSlice';
import { addSignature, changeIsDefaultSignature, deleteSignature, editSignature } from 'src/features/settings/utils';
import { refreshToken } from 'src/features/user/utils';
import { setCurrentUser } from 'src/features/user/userSlice';
import { CurrentUser } from 'src/features/user/constants';
import { defaultSignature, UserSignature } from 'src/utils/constant';

import SignatureDialog from './modals/SignatureDialog';
import { Typography } from 'src/components/new/Typography';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import { SignatureBox } from './SignatureBox';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { format } from '@react-input/mask';

interface SignatureBlockProps {
  currentUser: CurrentUser;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

export const SignatureBlock = ({ currentUser }: SignatureBlockProps) => {
  const dispatch: AppDispatch = useDispatch();

  const [showEditSignatureDialog, setshowEditSignatureDialog] = useState<boolean>(false);
  const [showAddSignatureForm, setShowAddSignatureForm] = useState(false);
  const [addSignatureSubmitted, setAddSignatureSubmitted] = useState(false);
  const [editSignatureSubmitted, setEditSignatureSubmitted] = useState(false);
  const [editSignatureId, setEditSignatureId] = useState(0);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const signatureState = useSelector((state: RootState) => state.settings.signature);
  const isSettingDefault = useSelector((state: RootState) => state.settings.signature.isSettingDefault);

  const onDelete = (id: number) => {
    dispatch(deleteSignature(id)).then((response) => {
      if (response.meta.requestStatus !== 'rejected') {
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              signatures: currentUser.signatures.filter((signature) => signature.id !== response.payload.id),
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(refreshToken());
      }
    });
  };

  const onMakeDefault = (defaultSignature: UserSignature) => {
    setEditSignatureId(defaultSignature.id);
    dispatch(loadData({ ...defaultSignature }));
    dispatch(changeIsDefaultSignature(!defaultSignature.isDefault)).then((response) => {
      if (response.meta.requestStatus !== 'rejected') {
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              signatures: currentUser.signatures.map((signature) =>
                signature.id === defaultSignature.id
                  ? { ...signature, isDefault: true }
                  : { ...signature, isDefault: false },
              ),
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(refreshToken());
      }
    });
  };

  const closeSignatureDialog = () => {
    setshowEditSignatureDialog(false);
  };

  const closeAddSignatureForm = () => {
    setShowAddSignatureForm(false);
  };

  const handleSignatureSubmit = async () => {
    dispatch(validateSignature());
    setEditSignatureSubmitted(true);
  };

  const handleAddSignatureSubmit = () => {
    dispatch(validateSignature());
    setAddSignatureSubmitted(true);
  };

  useEffect(() => {
    if (Object.values(signatureState.formDataErrors).some((error) => error)) {
      setAddSignatureSubmitted(false);
    }

    if (addSignatureSubmitted && !Object.values(signatureState.formDataErrors).some((error) => error)) {
      setAddSignatureSubmitted(false);
      dispatch(addSignature()).then((response) => {
        if (addSignature.fulfilled.match(response)) {
          dispatch(
            setCurrentUser({
              user: {
                ...currentUser,
                signatures: [...currentUser.signatures, response.payload],
              },
              accessToken: currentUser.accessToken,
            }),
          );
          dispatch(refreshToken());
          setShowSnackbarMsg({
            msg: 'Successfully added signature!',
            type: 'success',
          });
          setShowAddSignatureForm(false);
        }

        if (addSignature.rejected.match(response)) {
          setShowSnackbarMsg({
            msg: 'Something went wrong while creating signature!',
            type: 'error',
          });
        }
      });
    }
  }, [signatureState.formDataErrors, addSignatureSubmitted, dispatch]);

  useEffect(() => {
    if (Object.values(signatureState.formDataErrors).some((error) => error)) {
      setEditSignatureSubmitted(false);
    }

    if (editSignatureSubmitted && !Object.values(signatureState.formDataErrors).some((error) => error)) {
      setEditSignatureSubmitted(false);
      dispatch(editSignature()).then((response) => {
        if (editSignature.fulfilled.match(response)) {
          dispatch(
            setCurrentUser({
              user: {
                ...currentUser,
                signatures: currentUser.signatures.map((signature) =>
                  signature.id === editSignatureId
                    ? { ...signatureState.formData, phone: signatureState.formData.phone.replace(/\D/g, '') }
                    : signature,
                ),
              },
              accessToken: currentUser.accessToken,
            }),
          );
          dispatch(refreshToken());
          setShowSnackbarMsg({
            msg: 'Successfully modified signature!',
            type: 'success',
          });
          setshowEditSignatureDialog(false);
        }

        if (editSignature.rejected.match(response)) {
          setShowSnackbarMsg({
            msg: 'Something went wrong while modifying signature!',
            type: 'error',
          });
        }
      });
    }
  }, [signatureState.formDataErrors, editSignatureSubmitted, dispatch]);

  const onEdit = (signature: UserSignature) => {
    dispatch(
      loadData({
        ...signature,
        country: signature.country || 'United States',
        phone: format(signature.phone, { replacement: { _: /\d/ }, mask: '(___) ___-____' }),
      }),
    );
    setEditSignatureId(signature.id);
    dispatch(removeSuccessMsg());
    dispatch(clearErrors());
    setshowEditSignatureDialog(true);
  };

  const onAdd = () => {
    dispatch(loadData(defaultSignature));
    dispatch(removeSuccessMsg());
    dispatch(clearErrors());
    setShowAddSignatureForm(true);
  };

  return (
    <Box sx={{ padding: '2.5rem 1.5rem' }}>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem' }} fontWeight={400}>
        Signature Block
      </Typography>
      <Spacer h={24} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1.5rem',
        }}
      >
        {currentUser.signatures &&
          currentUser.signatures.map((signature) => (
            <SignatureBox
              signature={signature}
              key={signature.id}
              currentUser={currentUser}
              onDeleteClick={onDelete}
              onMakeDefaultClick={onMakeDefault}
              onEditClick={onEdit}
              isSettingDefault={isSettingDefault}
              editSignatureId={editSignatureId}
            />
          ))}
      </Box>
      <Box sx={{ maxWidth: '25rem', marginTop: '1.5rem' }}>
        <Button type="outline" onClick={onAdd}>
          <AddOutlinedIcon />
          <Spacer w={8} />
          Add Signature
        </Button>
      </Box>
      {showAddSignatureForm && (
        <SignatureDialog
          type="add"
          open={showAddSignatureForm}
          onClose={closeAddSignatureForm}
          onSubmit={handleAddSignatureSubmit}
        />
      )}
      {showEditSignatureDialog && (
        <SignatureDialog
          type="edit"
          open={showEditSignatureDialog}
          onClose={closeSignatureDialog}
          onSubmit={handleSignatureSubmit}
        />
      )}
    </Box>
  );
};
